import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plant, Storefront, Handshake, Package, Airplane, ShieldCheck,
  ArrowRight, TrendUp, ChartLineUp, Receipt, Truck, Wallet, FileText,
  Star, CheckCircle, MapPin, ChatCircleDots, Sparkle,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { LanguageToggle } from "@/components/LanguageToggle";
import { CountUp, Reveal } from "@/components/motion/Reveal";
import { WheatFieldHero } from "@/components/AnimatedField";
import { IndiaMandiLogo, IndiaMandiMark } from "@/components/Logo";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1696371269777-88d1ce71642c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const MANDI_IMG = "https://images.unsplash.com/photo-1764070254247-351def349875?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const TRUCK_IMG = "https://images.unsplash.com/photo-1605705658744-45f0fe8f9663?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const ROLE_ICONS = {
  Farmer: Plant,
  Mandi: Storefront,
  Trader: Handshake,
  Wholesaler: Package,
  Exporter: Airplane,
  Admin: ShieldCheck,
};

const SERVICES = [
  { icon: ShieldCheck, title: "Verified Counterparties", desc: "KYC-verified buyers & sellers; deal-level dispute resolution." },
  { icon: Wallet, title: "Digital Payments", desc: "Direct payments recorded on the platform with clear receipts." },
  { icon: Receipt, title: "GST-ready Billing", desc: "Auto-generated tax-invoices for every deal." },
  { icon: Truck, title: "Logistics", desc: "Verified partner network, tracked & time-bound delivery." },
  { icon: FileText, title: "Taxation (GST & ITR)", desc: "End-to-end filing at minimum charges via CA partners." },
  { icon: ChartLineUp, title: "Rate Intelligence", desc: "7-day trends, 4-season report cards, verified mandi rates." },
];

export default function Landing() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [ticker, setTicker] = useState([]);

  useEffect(() => {
    // language auto-detect first visit
    if (!localStorage.getItem("im.lang.detected")) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          () => localStorage.setItem("im.lang.detected", "1"),
          () => localStorage.setItem("im.lang.detected", "1"),
          { timeout: 3000 }
        );
      }
    }
    // load rate ticker
    (async () => {
      try {
        const { data } = await api.get("/rates/ticker");
        const items = data.items || [];
        setTicker(items);
      } catch {}
    })();
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="landing-page">
      {/* NAV */}
      <header className="glass-nav sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <IndiaMandiMark size={40} />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-stone-700">
            <a href="#how" className="hover:text-emerald-900 transition-colors">{t("nav.explore")}</a>
            <a href="#roles" className="hover:text-emerald-900 transition-colors">{t("nav.roles")}</a>
            <a href="#services" className="hover:text-emerald-900 transition-colors">{t("nav.services")}</a>
            <Link to="/trade" className="hover:text-emerald-900 transition-colors" data-testid="nav-trade">{t("nav.trade")}</Link>
            <a href="#blog" className="hover:text-emerald-900 transition-colors">{t("nav.blog")}</a>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            {user ? (
              <button
                onClick={() => nav("/app")}
                className="hidden sm:inline-flex items-center gap-1 px-4 py-2 rounded-md bg-emerald-900 hover:bg-emerald-800 text-white font-semibold text-sm transition-colors"
                data-testid="landing-go-dashboard-btn"
              >
                Dashboard <ArrowRight size={16} />
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden sm:inline-block px-3 py-2 text-sm font-semibold text-stone-700 hover:text-emerald-900 transition-colors"
                  data-testid="landing-login-btn"
                >
                  {t("nav.login")}
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-semibold text-sm shadow-md transition-colors"
                  data-testid="landing-get-started-btn"
                >
                  {t("nav.get_started")} <ArrowRight size={16} />
                </Link>
              </>
            )}
          </div>
        </div>
        {/* Ticker */}
        {ticker.length > 0 && (
          <div className="border-t border-stone-200 bg-emerald-950 text-white overflow-hidden">
            <div className="marquee-track flex gap-10 py-2 whitespace-nowrap">
              {[...ticker, ...ticker].map((it, i) => (
                <div key={i} className="inline-flex items-center gap-2 text-xs font-mono">
                  <span className="text-orange-400 font-bold uppercase tracking-widest">{it.commodity}</span>
                  <span className="text-stone-200">₹{it.rate.toLocaleString("en-IN")}/qtl</span>
                  <span className={it.pct >= 0 ? "text-emerald-400" : "text-red-400"}>
                    {it.pct >= 0 ? "▲" : "▼"} {Math.abs(it.pct).toFixed(2)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* HERO — photorealistic wheat field GIF */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center">
        {/* Wheat field backdrop */}
        <WheatFieldHero />
        {/* Left readability wash — brighter to counter the moodier sky in the new GIF */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/55 to-transparent lg:from-white/85 lg:via-white/30" />

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-8 pt-16 pb-24 grid lg:grid-cols-12 gap-12 items-center z-10">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-700 mb-6 bg-white/70 backdrop-blur px-3 py-1.5 rounded-full border border-orange-200/60">
                <Sparkle weight="fill" size={14} /> {t("hero.eyebrow")}
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight text-emerald-950 drop-shadow-[0_2px_18px_rgba(255,255,255,0.7)]">
                {t("hero.title_1")}
                <span className="block text-orange-600">{t("hero.title_2")}</span>
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-lg text-stone-700 leading-relaxed max-w-2xl">
                {t("hero.subtitle")}
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to={user ? "/app" : "/register"}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                  data-testid="hero-cta-primary"
                >
                  {t("hero.cta_primary")} <ArrowRight size={18} weight="bold" />
                </Link>
                <a
                  href="#how"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md border border-stone-300 hover:border-emerald-900 text-stone-800 hover:text-emerald-900 font-bold bg-white/90 backdrop-blur transition-colors"
                  data-testid="hero-cta-secondary"
                >
                  {t("hero.cta_secondary")}
                </a>
                <Link
                  to="/trade"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-emerald-900 hover:bg-emerald-800 text-white font-bold shadow-md hover:-translate-y-0.5 transition-all"
                  data-testid="hero-cta-trade"
                >
                  <Truck size={18} weight="bold" /> {t("hero.cta_trade")}
                </Link>
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
                <Stat value={22} suffix="+" label={t("hero.metric_1_sub")} big={t("hero.metric_1")} />
                <Stat value={50} suffix=" Cr+" label={t("hero.metric_2_sub")} big={t("hero.metric_2")} prefix="₹" />
                <Stat value={100} suffix="%" label={t("hero.metric_3_sub")} big={t("hero.metric_3")} />
              </div>
            </Reveal>
          </div>

          {/* Right: floating trade cards over the field */}
          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="relative h-[520px]">
              <div className="absolute right-0 top-4 bg-white/95 backdrop-blur rounded-xl shadow-2xl border border-white/60 p-4 w-56 lift">
                <div className="text-[10px] font-bold uppercase tracking-widest text-orange-600">Sample · Wheat · Ludhiana</div>
                <div className="mt-1 font-display text-2xl font-black text-emerald-900">₹2,380<span className="text-xs text-stone-500 font-normal">/qtl</span></div>
                <div className="text-xs text-emerald-600 font-semibold flex items-center gap-1"><TrendUp size={12} weight="bold" /> +2.4% today · sample</div>
              </div>

              <div className="absolute left-4 top-40 bg-emerald-950/95 backdrop-blur text-white rounded-xl shadow-2xl px-5 py-4 flex items-center gap-3 lift">
                <Receipt weight="fill" className="text-orange-400" size={22} />
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-orange-300">Sample GST Invoice</div>
                  <div className="font-display text-lg font-black">₹3,78,000</div>
                  <div className="text-[11px] text-emerald-200">Wheat · 150 qtl · Auto-generated</div>
                </div>
              </div>

              <div className="absolute right-8 bottom-16 bg-white/95 backdrop-blur rounded-xl shadow-2xl border border-white/60 px-4 py-3 flex items-center gap-3 lift">
                <div className="w-10 h-10 rounded-full bg-orange-500 text-white grid place-items-center font-black">RK</div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">Sample seller profile</div>
                  <div className="font-display text-sm font-bold text-stone-900 flex items-center gap-1">
                    <Star weight="fill" className="text-orange-500" size={14} /> 4.8 · Ramesh Kumar
                  </div>
                </div>
              </div>

              <div className="absolute left-0 bottom-0 bg-orange-600/95 backdrop-blur text-white rounded-xl shadow-2xl px-4 py-3 flex items-center gap-2 lift">
                <Truck weight="fill" size={20} />
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-orange-100">Sample · In Transit</div>
                  <div className="font-display text-sm font-bold">Ludhiana → Indore</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — Bento grid */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">{t("how.eyebrow")}</div>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-emerald-950 mt-3">{t("how.title")}</h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <p className="text-stone-600">{t("how.subtitle")}</p>
            </Reveal>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: Handshake, title: t("how.step_1_title"), desc: t("how.step_1_desc"), color: "bg-emerald-900 text-white", n: "01" },
            { icon: ShieldCheck, title: t("how.step_2_title"), desc: t("how.step_2_desc"), color: "bg-white text-stone-900", n: "02" },
            { icon: Truck, title: t("how.step_3_title"), desc: t("how.step_3_desc"), color: "bg-orange-600 text-white", n: "03" },
            { icon: CheckCircle, title: t("how.step_4_title"), desc: t("how.step_4_desc"), color: "bg-white text-stone-900", n: "04" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className={`lift rounded-2xl border border-stone-200 p-6 min-h-[240px] flex flex-col justify-between ${s.color}`} data-testid={`how-step-${i + 1}`}>
                <div className="flex items-center justify-between">
                  <s.icon weight="duotone" size={38} />
                  <span className="font-mono text-xs opacity-60">{s.n}</span>
                </div>
                <div>
                  <div className="font-display text-2xl font-bold tracking-tight">{s.title}</div>
                  <p className="mt-2 text-sm opacity-80">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ROLES */}
      <section id="roles" className="bg-emerald-950 text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <Reveal>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-400">{t("roles.eyebrow")}</div>
            <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-3 mb-12 max-w-2xl">{t("roles.title")}</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { key: "Farmer", desc: "Post produce, discover verified buyers, get paid on delivery." },
              { key: "Mandi", desc: "Manage sellers, log arrivals, run digital auctions." },
              { key: "Trader", desc: "Post requirements, match sellers, close deals fast." },
              { key: "Wholesaler", desc: "Bulk lots, storage tracking, distribution network." },
              { key: "Exporter", desc: "Shipment pipeline, compliance, destination country docs." },
              { key: "Admin", desc: "Verify users, resolve disputes, monitor platform health." },
            ].map((r, i) => {
              const Icon = ROLE_ICONS[r.key];
              return (
                <Reveal key={r.key} delay={i * 0.06}>
                  <div className="lift rounded-xl border border-emerald-800 bg-emerald-900/40 p-6" data-testid={`role-card-${r.key.toLowerCase()}`}>
                    <div className="w-12 h-12 rounded-md bg-orange-600 grid place-items-center mb-4">
                      <Icon weight="duotone" size={26} />
                    </div>
                    <div className="font-display text-2xl font-bold">{r.key}</div>
                    <p className="mt-2 text-sm text-emerald-100/80">{r.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
        <Reveal>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">{t("services.eyebrow")}</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-emerald-950 mt-3 mb-12 max-w-3xl">{t("services.title")}</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.05}>
              <div className="lift rounded-xl border border-stone-200 bg-white p-6" data-testid={`service-${i}`}>
                <s.icon weight="duotone" size={36} className="text-emerald-900" />
                <div className="mt-4 font-display text-xl font-bold text-stone-900">{s.title}</div>
                <p className="mt-2 text-sm text-stone-600 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TRUST — Rate trend + season card */}
      <section className="bg-stone-100 py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">{t("trust.eyebrow")}</div>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-emerald-950 mt-3">{t("trust.title")}</h2>
              <p className="mt-6 text-stone-600 leading-relaxed">
                Every rating comes from a completed deal. Every rate is a real transaction. Every mandi is verified.
              </p>
              <div className="mt-6 space-y-3">
                {[
                  "5-star ratings, only from delivered deals",
                  "7-day sparkline trends per commodity",
                  "4-season report card with % change",
                  "Verified Seller badges for KYC-cleared users",
                ].map((line, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle weight="fill" className="text-emerald-700" size={20} />
                    <span className="text-sm font-semibold text-stone-800">{line}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 lift rounded-2xl bg-white border border-stone-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-stone-500">Season Report Card</div>
                      <div className="font-display text-2xl font-bold text-stone-900 mt-1">Wheat • Last 4 Seasons</div>
                    </div>
                    <ChartLineUp weight="duotone" className="text-emerald-700" size={40} />
                  </div>
                  <div className="mt-5 grid grid-cols-4 gap-3">
                    {[
                      { s: "Kharif '24", r: "₹2,160", d: "Low" },
                      { s: "Rabi '24", r: "₹2,280", d: "Medium" },
                      { s: "Kharif '25", r: "₹2,340", d: "High" },
                      { s: "Rabi '25", r: "₹2,400", d: "Very High" },
                    ].map((sr, i) => (
                      <div key={i} className="rounded-md bg-stone-50 border border-stone-200 p-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">{sr.s}</div>
                        <div className="font-display text-lg font-bold text-emerald-900 mt-1">{sr.r}</div>
                        <div className="text-[10px] font-semibold text-orange-700 mt-1">{sr.d}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="lift rounded-2xl bg-emerald-900 text-white p-6">
                  <div className="flex items-center gap-2">
                    <Star weight="fill" className="text-orange-400" size={18} />
                    <span className="text-xs font-bold uppercase tracking-widest text-orange-300">Sample seller profile</span>
                  </div>
                  <div className="font-display text-2xl font-bold mt-3">Ramesh Kumar</div>
                  <div className="text-sm text-emerald-200">Ludhiana, Punjab</div>
                  <div className="mt-4 flex items-center gap-2">
                    <div className="text-3xl font-black text-orange-400">4.8</div>
                    <div className="text-xs text-emerald-100">/ 5 • 42 deals</div>
                  </div>
                </div>
                <div className="lift rounded-2xl bg-white border border-stone-200 p-6">
                  <div className="text-xs font-bold uppercase tracking-widest text-stone-500">Nearby Mandis</div>
                  <div className="font-display text-2xl font-bold text-stone-900 mt-2">22+ Yards</div>
                  <div className="text-sm text-stone-600 mt-1">across 15 states</div>
                  <div className="mt-4 flex items-center gap-2 text-emerald-700 text-sm font-semibold">
                    <MapPin weight="fill" size={16} /> Locate Nearby
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BLOG PREVIEW */}
      <BlogPreview />

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tight text-emerald-950">{t("cta.title")}</h2>
            <p className="mt-6 text-lg text-stone-600 max-w-2xl mx-auto">{t("cta.sub")}</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link
                to={user ? "/app" : "/register"}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-md bg-orange-600 hover:bg-orange-700 text-white font-bold shadow-xl transition-colors"
                data-testid="cta-register-btn"
              >
                {t("hero.cta_primary")} <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-emerald-950 text-emerald-100 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <IndiaMandiLogo size={38} />
              <div className="font-display font-black text-lg text-white">INDIA MANDI</div>
            </div>
            <p className="text-sm text-emerald-200/80">{t("footer.tagline")}</p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-orange-400 mb-3">Platform</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#how" className="hover:text-white">How it Works</a></li>
              <li><a href="#roles" className="hover:text-white">Roles</a></li>
              <li><a href="#services" className="hover:text-white">Services</a></li>
              <li><Link to="/trade" className="hover:text-white">State Rates and Trade Route</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-orange-400 mb-3">Company</div>
            <ul className="space-y-2 text-sm">
              <li>GLC Zone Private Limited</li>
              <li>Founder: Bishwajeet Dey</li>
              <li>hello@glczone.in</li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-orange-400 mb-3">Legal</div>
            <ul className="space-y-2 text-sm">
              <li>Privacy Policy</li>
              <li>Terms of Service</li>
              <li>GST Compliance</li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 mt-10 pt-6 border-t border-emerald-800 text-xs text-emerald-300/70 flex items-center justify-between">
          <div>© 2026 GLC Zone Pvt. Ltd. — All rights reserved.</div>
          <div className="font-mono">v1.0 · MVP</div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ value, prefix, suffix, big, label }) {
  return (
    <div className="relative">
      <div className="font-display font-black text-3xl text-emerald-900 drop-shadow-[0_2px_10px_rgba(255,255,255,0.95)]">
        {prefix && <span>{prefix}</span>}
        <CountUp value={value} />
        {suffix}
      </div>
      <div className="text-xs uppercase tracking-widest text-stone-700 font-semibold mt-1 drop-shadow-[0_1px_6px_rgba(255,255,255,0.9)]">{label}</div>
    </div>
  );
}

function BlogPreview() {
  const { t, lang } = useI18n();
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/blog?lang=${lang}`);
        setPosts((data || []).slice(0, 3));
      } catch {}
    })();
  }, [lang]);
  if (!posts.length) return null;
  return (
    <section id="blog" className="max-w-7xl mx-auto px-4 sm:px-8 py-24">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">{t("blog.eyebrow")}</div>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-emerald-950 mt-3">{t("blog.title")}</h2>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {posts.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08}>
            <Link to={`/blog/${p.slug}`} className="lift group block rounded-xl overflow-hidden border border-stone-200 bg-white" data-testid={`blog-card-${i}`}>
              {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-48 object-cover" />}
              <div className="p-5">
                <div className="text-[10px] font-bold uppercase tracking-widest text-orange-600">{p.category}</div>
                <div className="mt-2 font-display text-lg font-bold text-stone-900 leading-snug group-hover:text-emerald-900 transition-colors">{p.title}</div>
                <p className="mt-2 text-sm text-stone-600 line-clamp-3">{p.excerpt}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
